import { Controller } from "@hotwired/stimulus"
import { jquery } from 'utils/jquery'
// NProgress is loaded via Sprockets; access it lazily so this module does not
// throw when the global is absent (e.g. pages that don't include the full
// Sprockets bundle).
const nprogress = () => window.NProgress;

// Modern staged waiting overlay (voter-list-processing-overlay). Title per action + which
// stage row(s) are shown — 'importation' is the only two-stage action (import, then validate);
// 'validation' and 'copy_voters' are each a single stage.
const ACTION_TITLES = {
  importation: 'Importing your voter list',
  validation: 'Validating your voter list',
  copy_voters: 'Copying voters'
}

const ACTION_VISIBLE_STAGES = {
  importation: ['stageImporting', 'stageValidating'],
  validation: ['stageValidating'],
  copy_voters: ['stageCopying']
}

const STAGE_STATE_CLASSES = [
  'voter-list-processing-overlay__stage--pending',
  'voter-list-processing-overlay__stage--active',
  'voter-list-processing-overlay__stage--done'
]

// S2: equivalent of NProgress's own `html.nprogress-busy { pointer-events: none }` (see
// partials/_global.scss) for the modern overlay — blocks interaction with the rest of the page
// while import/validation is running. See processing_voters_component.scss for the paired rule
// that re-enables pointer-events on the overlay subtree itself.
const HTML_LOCK_CLASS = 'voter-list-processing-busy'
const OVERLAY_HIDDEN_CLASS = 'voter-list-processing-overlay--hidden'

// Normal (non-failure) status-poll cadence — unchanged from the original implementation.
const POLL_INTERVAL_MS = 500

// R1: bounded retries for BOTH ajax legs (status poll and completion request). Neither leg had a
// failure path at all before, so a single non-200 ended the flow silently and left the overlay +
// html lock up forever (see releaseAfterFailure). Budget is per-leg and per-streak: any successful
// response resets it, so a genuinely long import keeps polling indefinitely as long as the server
// keeps answering. Cap AND deadline are both enforced — the cap bounds a fast-failing server
// (~15.5s of backoff), the deadline bounds a slow-failing one (requests that each hang for
// seconds before erroring).
const MAX_REQUEST_RETRIES = 5
const RETRY_BACKOFF_MS = [500, 1000, 2000, 4000, 8000]
const RETRY_DEADLINE_MS = 30000
// Per-request ceiling. Without it jQuery waits forever (`timeout: 0` is its default and this app sets
// no `ajaxSetup`), and a request that never resolves reaches neither the retry cap nor the deadline
// above — both are only evaluated from a `.fail()` handler. 15s is comfortably longer than a healthy
// poll or page re-render and short enough that the bounded path still finishes well inside the
// deadline.
const REQUEST_TIMEOUT_MS = 15000

// Reuses the app's established async-failure idiom for this screen verbatim: a
// `span.inline_error#voter_list_error_description` appended to the container the component lives in
// — exactly what voter_list_imports/index.js.erb's own @processing_error branch renders. Sharing
// the id means the existing server-side cleanups (`$('#voter_list_error_description').remove()` in
// both index.js.erb and create.js.erb) also clear THIS message the moment the organizer retries.
const ERROR_MESSAGE_ID = 'voter_list_error_description'

// Both messages tell the organizer the one thing that is always true here: the browser lost the
// conversation, the work itself may well have finished, and a reload shows the real state.
const GIVE_UP_MESSAGES = {
  status: "ElectionBuddy couldn't check on your voter list — the connection was interrupted. "
    + 'The import may still have finished. Please reload this page to see its current status.',
  completion: "Your voter list finished processing, but ElectionBuddy couldn't refresh this page. "
    + 'Please reload the page to see the result.'
}

export default class extends Controller {
  // 'overlay' is emitted by VoterList::ProcessingOverlayCardComponent already (it renders
  // data-<data_controller>-target="overlay" on the overlay root); declaring it here is what lets
  // this controller dismiss its own overlay on failure.
  static targets = [
    'input', 'title', 'card', 'overlay', 'stageImporting', 'stageValidating', 'stageCopying',
    'progress', 'progressFill', 'progressLabel'
  ]

  connect() {
    this.torndown = false
    this.released = false
    this.pendingTimer = null
    this.retries = {
      status: { attempts: 0, since: null },
      completion: { attempts: 0, since: null }
    }

    let electionId = this.inputTarget.dataset.electionId;
    if (electionId === null || electionId === "") {
      return;
    }
    this.modernOverlay = this.inputTarget.dataset.modernOverlay === 'true';
    if (this.modernOverlay) {
      document.documentElement.classList.add(HTML_LOCK_CLASS)
      // A5: minimal focus management — move focus onto the dialog card itself so assistive tech
      // announces it; no full focus trap in this round.
      if (this.hasCardTarget) {
        this.cardTarget.focus()
      }
    } else {
      nprogress()?.start();
    }
    let scrollTopOption = this.inputTarget.dataset.scrollTopOption;
    if (scrollTopOption == 'true') {
      const $ = jquery();
      if ($) {
        $("html, body").animate({ scrollTop: $("#import_options_title").offset().top }, 'slow');
      }
    }
    this.scheduleVoterListProcessingStatusCheck(electionId)
  }

  // S2: disconnect-safe cleanup — always clears the html-level lock, whether that's because
  // processing finished (container swap removes this element from the DOM) or the controller is
  // torn down some other way. Harmless no-op for the legacy (non-modern) path, which never added
  // the class in the first place.
  disconnect() {
    // R1: also stop the clock. A pending poll/retry timer used to survive teardown and keep
    // hitting the server (and could fire a second completion request after the container swap).
    this.torndown = true
    this.clearPendingTimer()
    document.documentElement.classList.remove(HTML_LOCK_CLASS)
  }

  scheduleVoterListProcessingStatusCheck(electionId) {
    if (this.torndown || this.released) {
      return;
    }

    const $ = jquery();
    if (!$) {
      return;
    }

    // `$.ajax` with an explicit `timeout`, not a bare `$.get`: jQuery defaults to `timeout: 0`
    // (never), and there is no global `ajaxSetup` in this app. A request that HANGS rather than
    // erroring therefore fires no `.fail()` at all — and since both the retry cap and the deadline
    // are only ever evaluated inside `retryOrRelease`, which only a `.fail()` can reach, a hung
    // socket left the organizer trapped behind the modal exactly like the bug this file is meant to
    // end. With a timeout, jQuery rejects with textStatus 'timeout' and feeds the bounded path.
    $.ajax({ url: `voter_list_import_status/${electionId}`, timeout: REQUEST_TIMEOUT_MS }).done((processingStatus) => {
      if (this.torndown || this.released) {
        return;
      }

      // Shape guard BEFORE anything treats this as a successful poll. A session that expires mid-run
      // answers the 302-to-login transparently as HTTP 200 with an HTML body: `done` is undefined, so
      // the else-branch below re-arms the timer, and resetting the budget first meant neither the cap
      // nor the deadline could ever fire — the same trapped end-state, reached by a different door.
      if (typeof processingStatus !== 'object' || processingStatus === null) {
        this.retryOrRelease('status', () => this.scheduleVoterListProcessingStatusCheck(electionId))
        return;
      }

      // A reachable server resets this leg's failure budget — long imports poll as long as they
      // keep answering.
      this.resetRetries('status')

      let processingDone = processingStatus && processingStatus.done || false
      let action = processingStatus && processingStatus.action || ""

      if (this.modernOverlay) {
        this.renderModernStatus(processingStatus, action)
      }

      if (processingDone) {
        this.requestVoterListImportRefresh()
      } else {
        // S3: legacy (flag-off) message text stays scoped to this branch, exactly as before —
        // the modern path already rendered its own status above, independently of this text node.
        if (!this.modernOverlay) {
          if(action === 'validation') {
            $('#voter_list_processing_message').text('Validating voters.')
          } else if(action === 'importation') {
            $('#voter_list_processing_message').text('Importing and validating voters.')
          } else if(action === 'copy_voters') {
            $('#voter_list_processing_message').text('Copying voters.')
          }
        }
        this.pendingTimer = setTimeout(
          () => { this.scheduleVoterListProcessingStatusCheck(electionId) }, POLL_INTERVAL_MS
        )
      }
    }).fail(() => {
      this.retryOrRelease('status', () => this.scheduleVoterListProcessingStatusCheck(electionId))
    })
  }

  // The completion leg: the `done` payload's only job is to make the server re-render the page
  // (voter_list_imports#index -> index.js.erb), which is also what removes this component from the
  // DOM and so ends the run. Success behaviour is byte-identical to the original
  // `$.get('voter_list_import')` — the only addition is the failure branch.
  requestVoterListImportRefresh() {
    if (this.torndown || this.released) {
      return;
    }

    const $ = jquery();
    if (!$) {
      return;
    }

    $.ajax({ url: 'voter_list_import', timeout: REQUEST_TIMEOUT_MS }).fail((_jqXHR, textStatus) => {
      // `parsererror` is NOT a transport failure: the request returned 200 and jQuery then EVALUATED
      // index.js.erb (dataType 'script') and something threw partway through the page swap. Retrying
      // re-runs that response, and it is not idempotent — `EB.MultipleSelect.AddGroups` appends group
      // options to the question selects, so each retry duplicates them. Terminal instead: let the
      // organizer through and let them reload, rather than corrupting the form five more times.
      if (textStatus === 'parsererror') {
        this.releaseAfterFailure('completion')
        return;
      }

      this.retryOrRelease('completion', () => this.requestVoterListImportRefresh())
    })
  }

  retryOrRelease(leg, retry) {
    if (this.torndown || this.released) {
      return;
    }

    const budget = this.retries[leg]
    if (budget.since === null) {
      budget.since = Date.now()
    }

    const deadlineReached = (Date.now() - budget.since) >= RETRY_DEADLINE_MS
    if (budget.attempts >= MAX_REQUEST_RETRIES || deadlineReached) {
      this.releaseAfterFailure(leg)
      return;
    }

    const delay = RETRY_BACKOFF_MS[Math.min(budget.attempts, RETRY_BACKOFF_MS.length - 1)]
    budget.attempts += 1
    this.pendingTimer = setTimeout(retry, delay)
  }

  resetRetries(leg) {
    this.retries[leg] = { attempts: 0, since: null }
  }

  // R1: THE decision. When the retry budget is gone we let the organizer through instead of holding
  // them behind an aria-modal card on a pointer-events-disabled page. Rationale: the overlay is
  // purely informational — the work runs in a Sidekiq worker and has usually already COMMITTED by
  // the time this leg fails (that is exactly what happened in the one-word #index 500 that
  // motivated this: the import was done, the page behind the overlay was fine, and the only escape
  // was a manual reload). Trapping them protects nothing and hides a page that is already correct.
  // So: stop the clock, dismiss the overlay, drop the lock, and say what to do next.
  releaseAfterFailure(leg) {
    this.released = true
    this.clearPendingTimer()

    try {
      // Must run BEFORE dismissOverlay() — it anchors the message to this element's container,
      // which is only reachable while the element is still in the DOM.
      this.showFailureMessage(GIVE_UP_MESSAGES[leg])
    } finally {
      // Invariant: the html lock must never outlive the overlay. dismissOverlay() hides the overlay
      // and removes this element (which IS the overlay's ancestor) — unlockPage() then runs no
      // matter what, so there is no path that leaves `pointer-events: none` on <html>.
      try {
        this.dismissOverlay()
      } finally {
        this.unlockPage()
      }
    }
  }

  dismissOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add(OVERLAY_HIDDEN_CLASS)
    }
    // Same disposal the server already performs on a processing error
    // (`$('.voter_list_processing_voters_component').remove()` in index.js.erb): drop the whole
    // component, which takes the overlay markup (and the legacy status paragraph) with it. The
    // Stimulus disconnect() this triggers is an idempotent no-op on top of unlockPage() below.
    this.element.remove()
  }

  unlockPage() {
    document.documentElement.classList.remove(HTML_LOCK_CLASS)
    if (!this.modernOverlay) {
      // Legacy path: NProgress holds its own `html.nprogress-busy { pointer-events: none }` lock
      // (partials/_global.scss) — releasing ours is not enough there.
      nprogress()?.done()
    }
  }

  showFailureMessage(message) {
    const container = this.element.parentNode
    if (!container || !message) {
      return;
    }

    document.getElementById(ERROR_MESSAGE_ID)?.remove()

    const node = document.createElement('span')
    node.className = 'inline_error'
    node.id = ERROR_MESSAGE_ID
    node.setAttribute('role', 'alert')
    node.textContent = message
    container.appendChild(node)
  }

  clearPendingTimer() {
    if (this.pendingTimer) {
      clearTimeout(this.pendingTimer)
      this.pendingTimer = null
    }
  }

  renderModernStatus(processingStatus, action) {
    if (this.hasTitleTarget && ACTION_TITLES[action]) {
      this.titleTarget.textContent = ACTION_TITLES[action]
    }

    const stage = processingStatus && processingStatus.stage
    this.updateStages(action, stage)
    this.updateProgress(processingStatus && processingStatus.progress, stage)
  }

  updateStages(action, stage) {
    const visibleStages = ACTION_VISIBLE_STAGES[action] || []

    if (this.hasStageImportingTarget) {
      this.applyStageState(
        this.stageImportingTarget,
        visibleStages.includes('stageImporting'),
        stage === 'validating' ? 'done' : 'active'
      )
    }

    if (this.hasStageValidatingTarget) {
      const validatingState = action === 'importation'
        ? (stage === 'validating' ? 'active' : 'pending')
        : 'active'
      this.applyStageState(this.stageValidatingTarget, visibleStages.includes('stageValidating'), validatingState)
    }

    if (this.hasStageCopyingTarget) {
      this.applyStageState(this.stageCopyingTarget, visibleStages.includes('stageCopying'), 'active')
    }
  }

  applyStageState(element, visible, state) {
    element.classList.toggle('voter-list-processing-overlay__stage--hidden', !visible)
    if (!visible) {
      return;
    }
    element.classList.remove(...STAGE_STATE_CLASSES)
    element.classList.add(`voter-list-processing-overlay__stage--${state}`)
  }

  // S1: the import phase's `progress` payload is never cleared once the worker transitions into
  // the 'validating' stage (ValidateAction has no per-validator hook — indeterminate only, see the
  // spec), so a stale determinate `progress` object would otherwise render a stuck 100% (or a
  // stalled <100%, since the legacy row count can over-count) bar throughout validation. Force the
  // indeterminate shimmer whenever `stage === 'validating'`, regardless of `progress`.
  updateProgress(progress, stage) {
    if (!this.hasProgressTarget) {
      return;
    }

    // A4: only treat this as a determinate payload when it actually looks like one — guards
    // against calling toLocaleString on undefined if `progress`/`processed` is missing/malformed.
    const isDeterminate = stage !== 'validating' &&
      !!progress &&
      typeof progress.processed === 'number' &&
      progress.total > 0

    if (isDeterminate) {
      const { processed, total } = progress
      const percent = Math.min(100, Math.round((processed / total) * 100))
      this.progressTarget.classList.add('voter-list-processing-overlay__progress--determinate')
      this.progressTarget.classList.remove('voter-list-processing-overlay__progress--indeterminate')

      if (this.hasProgressFillTarget) {
        this.progressFillTarget.style.width = `${percent}%`
        this.progressFillTarget.setAttribute('role', 'progressbar')
        this.progressFillTarget.setAttribute('aria-valuenow', String(percent))
        this.progressFillTarget.setAttribute('aria-valuemin', '0')
        this.progressFillTarget.setAttribute('aria-valuemax', '100')
      }
      if (this.hasProgressLabelTarget) {
        this.progressLabelTarget.textContent = `${percent}% — ${processed.toLocaleString()} of ${total.toLocaleString()} rows`
      }
    } else {
      this.progressTarget.classList.add('voter-list-processing-overlay__progress--indeterminate')
      this.progressTarget.classList.remove('voter-list-processing-overlay__progress--determinate')

      if (this.hasProgressFillTarget) {
        this.progressFillTarget.style.width = ''
        this.progressFillTarget.removeAttribute('role')
        this.progressFillTarget.removeAttribute('aria-valuenow')
        this.progressFillTarget.removeAttribute('aria-valuemin')
        this.progressFillTarget.removeAttribute('aria-valuemax')
      }
      if (this.hasProgressLabelTarget) {
        this.progressLabelTarget.textContent = ''
      }
    }
  }
}
